import { Box, Card, Grid, Stack, Typography } from '@mui/material';

import { SectionTitle } from '~/components/common/SectionTitle';

import { ContractCarKpiTable } from './ContractCarKpiTable';
import type { ContractReview } from './types';

const KpiStat = ({ label, value }: { label: string; value: string }) => (
  <Box sx={{ minWidth: 110 }}>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="body2">{value}</Typography>
  </Box>
);

type Props = {
  contract: ContractReview;
};

export const ContractKpisTab = ({ contract }: Props) => {
  const { kpiTargets } = contract;
  const hasKpiTargets =
    kpiTargets.maintenanceCompletionTarget > 0 ||
    kpiTargets.maintenanceHoursTarget > 0 ||
    kpiTargets.uptimeTarget > 0 ||
    kpiTargets.onTimeResponseTargetPct > 0 ||
    kpiTargets.callbackRateTarget !== null ||
    kpiTargets.emergencyCallRateTarget !== null;

  return (
    <Box sx={{ p: 3, width: '100%' }}>
      <Grid container spacing={3}>
        <Grid size={{ xs: 12 }}>
          <Card variant="outlined" sx={{ p: 2 }}>
            <SectionTitle>Contract KPI Targets</SectionTitle>
            {hasKpiTargets ? (
              <Stack direction="row" spacing={2} sx={{ mt: 1 }} flexWrap="wrap" useFlexGap>
                <KpiStat
                  label="Maintenance"
                  value={
                    kpiTargets.maintenanceCompletionTarget > 0
                      ? `${kpiTargets.maintenanceCompletionTarget} visits/yr`
                      : '—'
                  }
                />
                <KpiStat
                  label="Maintenance hours"
                  value={
                    kpiTargets.maintenanceHoursTarget > 0
                      ? `${kpiTargets.maintenanceHoursTarget} hrs/yr`
                      : '—'
                  }
                />
                <KpiStat
                  label="Uptime target"
                  value={kpiTargets.uptimeTarget > 0 ? `${kpiTargets.uptimeTarget}%` : '—'}
                />
                <KpiStat
                  label="On-time response"
                  value={
                    kpiTargets.onTimeResponseTargetPct > 0
                      ? `${kpiTargets.onTimeResponseTargetPct}%`
                      : '—'
                  }
                />
                <KpiStat
                  label="Callback rate"
                  value={
                    kpiTargets.callbackRateTarget !== null
                      ? `≤ ${kpiTargets.callbackRateTarget}/car/yr`
                      : '—'
                  }
                />
                <KpiStat
                  label="Emg. call rate"
                  value={
                    kpiTargets.emergencyCallRateTarget !== null
                      ? `≤ ${kpiTargets.emergencyCallRateTarget}/car/yr`
                      : '—'
                  }
                />
              </Stack>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                No KPI targets specified on this contract.
              </Typography>
            )}
          </Card>
        </Grid>

        <Grid size={{ xs: 12 }}>
          <Card variant="outlined" sx={{ p: 2 }}>
            <SectionTitle>Per-Car KPI Targets</SectionTitle>
            <Box sx={{ mt: 1 }}>
              <ContractCarKpiTable contract={contract} />
            </Box>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};
